import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import styles from '@components/Select/Select.styles';
import { TProps } from '@components/Select/typescript';
import ExpandMore from '@components/icons/components/ExpandMore';
import Icon from '@components/icons/Icon';
const DEFAULT_DROPDOWN_MAX_HEIGHT = '145px';
const SEARCHABLE_DROPDOWN_MAX_HEIGHT = '290px';
const Select = ({
  value,
  onChange = () => undefined,
  placeholder,
  options = [],
  addSx = {},
  name = '',
  readonly = false,
  disabled = false,
  className = '',
  cy,
  showError = false,
  error = '',
  id,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  required = false,
  searchable = false,
  searchPlaceholder = '',
  noResultsText = ''
}: TProps) => {
  const [active, setActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const testId = cy || 'select';
  const changeValue = useCallback((event, newValue) => {
    if (readonly || disabled) return;
    event.preventDefault();
    onChange(newValue);
    setActive(false);
    setSearchQuery('');
  }, [disabled, onChange, readonly, setActive]);
  const dropdownToggle = useCallback(() => {
    if (readonly || disabled) return;
    setActive(state => !state);
  }, [readonly, disabled, setActive]);
  const handleValueClick = useCallback(() => {
    if (searchable && active) return;
    dropdownToggle();
  }, [active, dropdownToggle, searchable]);
  const renderOptions = useMemo(() => {
    if (!options.length) return null;
    return options.map(option => <option key={option.name} value={option.value} data-testid={`${testId}-native-option`}>
        {option.name}
      </option>);
  }, [options, testId]);
  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter(option => {
      const name = option.name.toLowerCase();
      const optionValue = option.value.toString().toLowerCase();
      return name.includes(normalizedQuery) || optionValue.includes(normalizedQuery);
    });
  }, [options, searchQuery, searchable]);
  const renderDropdownOptions = useMemo(() => {
    if (!filteredOptions.length) return null;
    return filteredOptions.map(option => {
      const isSelected = value === option.value;
      return <li key={option.name} role="option" aria-selected={isSelected} data-testid={`${testId}-option`}>
          <button sx={styles.selectDropdownElementButton(isSelected)} type="button" onClick={event => changeValue(event, option.value)} role="option" aria-selected={isSelected} data-testid={`${testId}-option-button`}>
            {option.name}
          </button>
        </li>;
    });
  }, [changeValue, filteredOptions, value, testId]);
  const valueName = useMemo(() => {
    const foundOption = options?.find(option => option.value === value);
    if (foundOption) return foundOption.name;
    return '';
  }, [options, value]);
  useEffect(() => {
    const onOutsideClick = (event: MouseEvent) => {
      let targetElement = event.target as HTMLElement;
      do {
        if (!dropdownRef.current) return;
        const {
          children
        } = dropdownRef.current;
        if (Array.from(children).some(child => targetElement === child)) return;
        targetElement = targetElement.parentElement as HTMLElement;
      } while (targetElement);
      setActive(false);
      setSearchQuery('');
    };
    window.addEventListener('click', onOutsideClick);
    return () => window.removeEventListener('click', onOutsideClick);
  }, []);
  useEffect(() => {
    if (!active) {
      setSearchQuery('');
      return;
    }
    if (searchable) {
      searchInputRef.current?.focus();
    }
  }, [active, searchable]);
  const selectId = id || `select-${name || placeholder || 'field'}`;
  const errorId = showError ? `${selectId}-error` : undefined;
  return <>
      <div sx={{
      ...styles.selectWrapper(readonly, disabled, showError),
      ...addSx
    }} className={className} data-testid={`${testId}-wrapper`} ref={dropdownRef}>
        <select sx={styles.selectNative} name={name} defaultValue={value} id={selectId} aria-label={ariaLabel} aria-labelledby={ariaLabelledBy} aria-describedby={ariaDescribedBy || errorId} aria-invalid={showError} aria-expanded={active} aria-haspopup="listbox" aria-required={required} required={required} role="combobox" data-testid={`${testId}-native`}>
          {renderOptions}
        </select>
        <div sx={styles.selectValue(!valueName, readonly, disabled, showError, searchable)} onClick={handleValueClick} role="button" tabIndex={searchable || disabled ? -1 : 0} aria-label={ariaLabel || placeholder} aria-labelledby={ariaLabelledBy} aria-describedby={ariaDescribedBy || errorId} aria-expanded={active} aria-haspopup="listbox" aria-required={required} data-testid={`${testId}-value`} onKeyDown={e => {
        if (searchable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          dropdownToggle();
        }
      }}>
          {!!placeholder && <span sx={styles.selectPlaceholder(!valueName, active, showError)} data-testid={`${testId}-placeholder`}>
              {placeholder}
            </span>}
          {searchable ? <input ref={searchInputRef} type="text" value={active ? searchQuery : valueName} placeholder={active ? searchPlaceholder : ''} aria-label={searchPlaceholder || ariaLabel || placeholder} autoComplete="off" disabled={disabled} readOnly={readonly} data-cy={`${testId}-search`} data-testid={`${testId}-search`} sx={styles.selectValueInput} onFocus={() => {
          if (!readonly && !disabled) {
            setActive(true);
          }
        }} onClick={event => {
          event.stopPropagation();
          if (!active && !readonly && !disabled) {
            setActive(true);
          }
        }} onChange={event => {
          if (readonly || disabled) return;
          setSearchQuery(event.target.value);
          if (!active) {
            setActive(true);
          }
        }} onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        }} /> : !!valueName && <span data-testid={`${testId}-value-text`}>{valueName}</span>}
          <Icon size="24px" addSx={styles.selectDropdownIcon(active)} color={disabled ? 'darkGrey300' : 'darkGrey500'} cy={`${testId}-dropdown-icon`}>
            <ExpandMore />
          </Icon>
        </div>
        <div sx={styles.selectDropdownPanel(active, searchable ? SEARCHABLE_DROPDOWN_MAX_HEIGHT : DEFAULT_DROPDOWN_MAX_HEIGHT)} aria-hidden={!active} data-testid={`${testId}-dropdown`}>
          <ul sx={styles.selectDropdownElementWrapper} role="listbox" aria-labelledby={ariaLabelledBy}>
            {renderDropdownOptions}
            {searchable && !filteredOptions.length && <li sx={styles.selectNoResults} data-testid={`${testId}-no-results`}>
                {noResultsText}
              </li>}
          </ul>
        </div>
      </div>
      {showError && <div sx={styles.selectError(showError)} id={errorId} role="alert" data-testid={`${testId}-error`}>
          {error}
        </div>}
    </>;
};
export default observer(Select);